import { useEffect, useState } from 'react';

const CANADIAN_PROVINCE_CODES = new Set([
  'AB',
  'BC',
  'MB',
  'NB',
  'NL',
  'NS',
  'NT',
  'NU',
  'ON',
  'PE',
  'QC',
  'SK',
  'YT',
]);

export interface CityOption {
  value: string;
  label: string;
}

type CityModule = typeof import('country-state-city');

let cachedModulePromise: Promise<CityModule> | null = null;

const loadCityModule = (): Promise<CityModule> => {
  if (!cachedModulePromise) {
    cachedModulePromise = import('country-state-city');
  }
  return cachedModulePromise;
};

export const useUSCanadaCities = (stateCode: string | undefined) => {
  const [cities, setCities] = useState<CityOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!stateCode) {
      setCities([]);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    loadCityModule()
      .then((mod) => {
        if (cancelled) return;
        const countryCode = CANADIAN_PROVINCE_CODES.has(stateCode) ? 'CA' : 'US';
        const list = mod.City.getCitiesOfState(countryCode, stateCode) ?? [];
        const options = list
          .map((c) => ({ value: c.name, label: c.name }))
          .sort((a, b) => a.label.localeCompare(b.label));
        setCities(options);
        setIsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setCities([]);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [stateCode]);

  return { cities, isLoading };
};
